/* eslint-disable operator-linebreak */

import _ from 'lodash';
import React, {
    useState,
    useRef,
    useEffect
} from 'react';
import PropTypes from 'prop-types';

import EditCompanySet from './EditCompanySet.jsx';

import {
    useGetCompanySets,
    useDeleteCompanySet,
    useGetCompanySetUsageCounts
} from '../../hooks/companySetHooks.js';

import RIQButton from '../../../../components/RIQButton.jsx';

import {
    RIQInfoPopover,
    useRIQTooltip
} from '../../../../components/RIQPopover.jsx';


const MAX_COMPANY_SET_NAME_LENGTH = 100;

CompanySet.propTypes = {
    companySet: PropTypes.object.isRequired,
    isSelected: PropTypes.bool.isRequired,
    onSelectCompanySet: PropTypes.func.isRequired,
    onEditCompanySet: PropTypes.func.isRequired
};

function CompanySetDeleteConfirmMessage({companySet}) {
    const {data: usageCounts, isLoading} = useGetCompanySetUsageCounts(companySet.id);

    let message;
    if (usageCounts?.num_reports || usageCounts?.num_dashboards) {
        message = `Deleting this Company Set will remove it from ${_.compact([
            usageCounts?.num_reports && `${usageCounts?.num_reports} Scheduled Export${usageCounts?.num_reports > 1 ? 's' : ''}`,
            usageCounts?.num_dashboards && `${usageCounts?.num_dashboards} Custom Dashboard${usageCounts?.num_dashboards > 1 ? 's' : ''}`
        ]).join(' and ')}.`;
    } else if (!isLoading) {
        message = `Deleting this company set cannot be undone.`;
    }
    return (
        <>
            <hr
                className='tlw-mx-0 tlw-my-4'
            />
            {message}
            {isLoading &&
                <div>
                    <span className='fa fa-circle-o-notch fa-spin'></span>
                </div>
            }
        </>
    );
}

function CompanySet({companySet, isSelected, onSelectCompanySet, onEditCompanySet, scrollIntoView}) {

    const wrapperRef = useRef(null);
    const {mutate: deleteCompanySet, isLoading} = useDeleteCompanySet();

    const [isHovering, setIsHovering] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isInDeleteMode, setIsInDeleteMode] = useState(false);

    const {setTriggerRef: editTriggerRef, tooltipNode: editTooltipNode} = useRIQTooltip({
        text: 'Edit Company Set Name',
        placement: 'top',
        delayShow: 200
    });
    const {setTriggerRef: deleteTriggerRef, tooltipNode: deleteTooltipNode} = useRIQTooltip({
        text: 'Delete Company Set',
        placement: 'top',
        delayShow: 200
    });

    useEffect(() => {
        if (scrollIntoView && wrapperRef) {
            wrapperRef.current.scrollIntoView({
                behavior: 'smooth'
            });
        }
    }, [scrollIntoView]);

    const handleClickOutside = event => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
            setIsInDeleteMode(false);
        }
    };

    useEffect(() => {
        // This effect is how the user exits delete mode - by clicking outside the delete button. If the user clicks on the delete button, `onDeletedClicked` will get
        // the event first and call `stopPropagation`.
        document.addEventListener('click', handleClickOutside, false);
        return () => {
            document.removeEventListener('click', handleClickOutside, false);
        };
    });

    function onConfirmDeleteCompanySet() {
        deleteCompanySet({
            companySetId: companySet.id
        });
    }

    function onDeletedClicked(event) {
        event.stopPropagation();
        onSelectCompanySet(companySet);
        setIsInDeleteMode(true);
    }

    function onKeyDown(event) {
        if (isFocused && event.key === 'Enter') {
            onSelectCompanySet(companySet);
        }
    }

    return (
        <div
            role='radio'
            tabIndex={0}
            aria-checked={isSelected}
            className={`${(isSelected ? 'tlw-border-blue-200 tlw-bg-blue-400 tlw-from-blue-400 tlw-to-blue-200 tlw-text-white' : 'tlw-border-gray-300')} tlw-flex tlw-cursor-pointer tlw-flex-col tlw-border-0 tlw-border-b tlw-border-solid tlw-px-7.5 tlw-py-5`}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onClick={() => onSelectCompanySet(companySet)}
            onKeyDown={onKeyDown}
            ref={wrapperRef}
            // eslint-disable-next-line object-curly-newline
            style={isSelected ? {textShadow: 'rgb(56 153 225) 0px -1px 0px'} : {}} // tailwind doesn't support text-shadow
        >

            <div
                className='tlw-flex tlw-items-center'>
                <div className='tlw-grow tlw-overflow-hidden tlw-text-ellipsis'>
                    {companySet.name}
                </div>

                <RIQButton
                    key='delete_confirmed'
                    className={`tlw-flex tlw-items-center tlw-px-2.5 tlw-py-0.5 tlw-text-sm tlw-leading-normal tlw-text-red-400 ${(isSelected && isInDeleteMode) ? '' : 'tlw-invisible'}`}
                    disabled={isLoading}
                    onClick={() => onConfirmDeleteCompanySet(companySet)}>
                    <span className='fa fa-trash-o' />
                    &nbsp; Delete
                </RIQButton>

                {!isInDeleteMode && <>
                    <RIQButton
                        key='edit'
                        className={`tlw-mr-5 tlw-px-2.5 tlw-py-0.5 tlw-text-sm tlw-leading-normal ${(isSelected || isHovering) ? '' : 'tlw-invisible'}`}
                        onClick={() => onEditCompanySet(companySet)}
                        ref={editTriggerRef}>
                        <span className='fa fa-edit' />
                    </RIQButton>
                    {editTooltipNode}

                    <RIQButton
                        key='delete'
                        className={`tlw-px-2.5 tlw-py-0.5 tlw-text-sm tlw-leading-normal ${(isSelected || isHovering) ? '' : 'tlw-invisible'}`}
                        onClick={onDeletedClicked}
                        ref={deleteTriggerRef}>
                        <span className='fa fa-trash-o' />
                    </RIQButton>
                    {deleteTooltipNode}
                </>}
            </div>

            { isSelected && isInDeleteMode &&
                <CompanySetDeleteConfirmMessage
                    companySet={companySet}
                />
            }
        </div>
    );
}

CompanySets.propTypes = {
    className: PropTypes.string,
    selectedCompanySetName: PropTypes.string,
    updateSelectedCompanySetName: PropTypes.func.isRequired,
    heightClass: PropTypes.string
};


export default function CompanySets({className, selectedCompanySetName, updateSelectedCompanySetName, heightClass}) {

    const {data: companySets} = useGetCompanySets();

    const [isCreatingNewSet, setIsCreatingNewSet] = useState(false);
    const [editingCompanySetName, setEditingCompanySetName] = useState(null);

    const [newlyCreatedCompanySetName, setNewlyCreatedCompanySetName] = useState(null);

    function onCreateCompanySet() {
        setIsCreatingNewSet(true);
        updateSelectedCompanySetName(null);
        setEditingCompanySetName(null);
    }

    function onUpdateCompanySet(companySetName) {
        setIsCreatingNewSet(false);
        updateSelectedCompanySetName(companySetName);
        setEditingCompanySetName(null);
    }

    function onCreateNewCompanySet(companySetName) {
        onUpdateCompanySet(companySetName);
        setNewlyCreatedCompanySetName(companySetName);
    }

    function onCancelNewCompanySet() {
        setIsCreatingNewSet(false);
        setEditingCompanySetName(null);
    }

    function onSelectCompanySet(companySet) {
        updateSelectedCompanySetName(companySet.name);
        setEditingCompanySetName(editingCompanySetName => {
            if (editingCompanySetName === companySet.name) {
                return companySet.name;
            }

            return null;
        });
        setIsCreatingNewSet(false);
    }

    function onEditCompanySet(companySet) {
        setEditingCompanySetName(companySet.name);
    }

    function validateCompanySetName(companySetName) {
        if (_.isEmpty(companySetName)) {
            return 'Must be non-empty';
        }

        if (companySetName === editingCompanySetName) {
            return null;
        }

        if (companySetName.length >= MAX_COMPANY_SET_NAME_LENGTH) {
            return `Must be under ${MAX_COMPANY_SET_NAME_LENGTH} characters.`;
        }

        const isDuplicate = _.some(companySets, {
            name: companySetName
        });

        if (isDuplicate) {
            return `Company set names must be unique.`;
        }
    }

    return (
        <div className={className}>
            <div className='tlw-flex tlw-items-center tlw-border tlw-border-l-0 tlw-border-t-0 tlw-border-solid tlw-border-gray-300 tlw-bg-gray-150 tlw-px-7.5 tlw-py-5 tlw-text-gray-800'>
                <h5 className='tlw-m-0 tlw-grow'>
                    Company Sets
                    <RIQInfoPopover
                        description='Create a Company Set and then add companies to it using the checkboxes in the right column.'
                    />
                </h5>
                <button
                    className='tlw-rounded-sm tlw-border tlw-border-solid tlw-border-gray-600 tlw-bg-white tlw-px-2.5 tlw-py-0.5 tlw-text-sm tlw-leading-[1.5] tlw-text-gray-800 tlw-shadow-sm hover_tlw-border-gray-600 hover_tlw-bg-gray-100 focus_tlw-border-gray-600 focus_tlw-bg-gray-100 active_tlw-border-gray-400 active_tlw-bg-gray-200'
                    onClick={onCreateCompanySet}>
                    <span className='fa fa-plus fa-fw' />
                    Create Set
                </button>
            </div>

            <ul className={`tlw-m-0 tlw-overflow-auto tlw-p-0 ${heightClass} tlw-list-none tlw-border tlw-border-b-0 tlw-border-l-0 tlw-border-t-0 tlw-border-solid tlw-border-gray-300`}>

                {!isCreatingNewSet && _.isEmpty(companySets) &&
                    <li className='tlw-mt-[30px] tlw-text-center'>
                        <h1 className='tlw-mt-5 tlw-text-gray-500'>
                            <span className='fa fa-filter fa-fw' />
                        </h1>
                        <h4 className='tlw-m-0 tlw-leading-base'>Create a Company Set, then select companies.</h4>

                        <RIQButton
                            className='tlw-my-10 tlw-px-6 tlw-py-3'
                            variant='primary'
                            onClick={onCreateCompanySet}>
                            <span className='fa fa-plus fa-fw' />
                            Create Set
                        </RIQButton>
                    </li>
                }

                {isCreatingNewSet &&
                    <li
                        className='tlw-p-0'
                    >
                        <EditCompanySet
                            validateCompanySetName={validateCompanySetName}
                            onUpdateCompanySet={onCreateNewCompanySet}
                            onCancelCompanySetName={onCancelNewCompanySet}
                        />
                    </li>}

                {_.sortBy(companySets, companySet => companySet.name.toLocaleLowerCase()).map(companySet => (
                    <li
                        key={companySet.name}
                        className='tlw-p-0'
                    >
                        { editingCompanySetName && editingCompanySetName === companySet.name
                            ? <EditCompanySet
                                currentName={companySet.name}
                                companySetId={companySet.id}
                                validateCompanySetName={validateCompanySetName}
                                onUpdateCompanySet={onUpdateCompanySet}
                                onCancelCompanySetName={onCancelNewCompanySet}
                            />

                            : <CompanySet
                                companySet={companySet}
                                isSelected={companySet.name === selectedCompanySetName}
                                onSelectCompanySet={onSelectCompanySet}
                                onEditCompanySet={onEditCompanySet}
                                scrollIntoView={newlyCreatedCompanySetName === companySet.name}
                            />
                        }
                    </li>
                ))}
            </ul>
        </div>
    );
}
